/**
 * Classement « humains uniquement » — client HTTP.
 *
 * Le worker Cloudflare sert le classement à la RACINE du même hôte que l'arène
 * (`https://bolt.21pay.org/leaderboard/*`), là où l'économie Lightning vit sous
 * `/api/v1/arena/*` : d'où le `ArenaClient.baseUrl()` passé au constructeur.
 *
 * Deux règles tenues côté client :
 *  - le `session_token` ne sort JAMAIS vers l'UI ; il ne part qu'en corps de
 *    `POST /leaderboard/session`, exactement comme pour les autres appels arène.
 *  - l'identifiant public (`playerId`) est le sha256 du token, la même dérivation
 *    que `worker/src/identity.ts` — calculé localement, jamais persisté.
 */

import { sha256Hex } from './crypto';

export type LeaderboardPeriod = 'daily' | 'weekly' | 'alltime';

/** Ligne du classement, telle que renvoyée par `worker/src/leaderboard.ts`. */
export interface LeaderboardEntry {
  pubkey: string;
  name: string;
  elo: number;
  sats: number;
  kills: number;
  deaths: number;
  kdRatio: number;
  wins: number;
  losses: number;
  rank: number;
  lastActive: number;
}

/** Ce que le HUD doit afficher : le top + la position du joueur local. */
export interface LeaderboardView {
  entries: LeaderboardEntry[];
  /** id public du joueur local (session payée) — sinon null */
  playerId: string | null;
  /** rang général quand le joueur n'est pas dans le top affiché (0 = inconnu) */
  myRank: number;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Normalisation défensive : l'UI formate des nombres, jamais du JSON brut. */
function toEntry(raw: unknown): LeaderboardEntry {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    pubkey: typeof r.pubkey === 'string' ? r.pubkey : '',
    name: typeof r.name === 'string' && r.name ? r.name : '???',
    elo: Math.round(num(r.elo, 1200)),
    sats: Math.round(num(r.sats)),
    kills: Math.round(num(r.kills)),
    deaths: Math.round(num(r.deaths)),
    kdRatio: num(r.kdRatio),
    wins: Math.round(num(r.wins)),
    losses: Math.round(num(r.losses)),
    rank: Math.round(num(r.rank)),
    lastActive: num(r.lastActive),
  };
}

export class LeaderboardClient {
  private base: string;

  /** @param origin racine du worker (ex. `https://bolt.21pay.org`) */
  constructor(origin: string) {
    this.base = origin.replace(/\/$/, '') + '/leaderboard';
  }

  private async get(path: string, timeoutMs = 8000): Promise<unknown> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(this.base + path, { signal: ctrl.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } finally {
      clearTimeout(t);
    }
  }

  /** Top classé par frags (les bots n'y sont pas : filtre serveur). */
  async topKillers(period: LeaderboardPeriod, limit = 10): Promise<LeaderboardEntry[]> {
    const d = await this.get(`/top-killers?period=${period}&limit=${limit}`);
    return Array.isArray(d) ? d.map(toEntry) : [];
  }

  /** Top classé par sats gagnés (même format). */
  async topEarners(period: LeaderboardPeriod, limit = 10): Promise<LeaderboardEntry[]> {
    const d = await this.get(`/top-earners?period=${period}&limit=${limit}`);
    return Array.isArray(d) ? d.map(toEntry) : [];
  }

  /** Rang général (tri ELO côté serveur). 0 = non classé / indisponible. */
  async rank(playerId: string, period: LeaderboardPeriod): Promise<number> {
    const d = (await this.get(`/rank/${playerId}?period=${period}`)) as { rank?: unknown };
    return Math.max(0, Math.round(num(d?.rank)));
  }

  /**
   * Identifiant public dérivé du token payé — identique à celui que le worker
   * recalcule (`identity.sha256Hex`). Sert uniquement à repérer sa propre ligne.
   */
  playerId(sessionToken: string): string {
    return sha256Hex(sessionToken);
  }

  /**
   * Enregistre la session payée au classement. Fire & forget : le worker peut
   * ne pas être déployé, le jeu ne doit jamais s'en apercevoir.
   * Les compteurs sont relus par le worker auprès du serveur Lightning — le
   * client n'envoie que son token et son pseudo.
   */
  async registerSession(sessionToken: string, nickname: string): Promise<boolean> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 8000);
    try {
      const r = await fetch(this.base + '/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, nickname }),
        signal: ctrl.signal,
      });
      return r.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(t);
    }
  }
}
